import { useState } from 'react';
import {
    type PayoutOrder,
    useApprovePayoutOrder,
    useDeclinePayoutOrder,
    useInvestigatePayoutOrder,
} from '~/entities/payout';

type ConfirmationAction = 'investigate' | 'approve' | 'decline';
type OrderId = NonNullable<PayoutOrder['id']>;

interface UseOrderActionConfirmationParams {
    order: PayoutOrder;
    onApprove?: VoidFunction;
    onDecline?: VoidFunction;
}

interface ConfirmationActionConfig {
    title: string;
    message: string;
    confirmText: string;
    loading: boolean;
    submit: (orderId: OrderId) => void;
}

export const useOrderActionConfirmation = ({ order, onApprove, onDecline }: UseOrderActionConfirmationParams) => {
    const [confirmationAction, setConfirmationAction] = useState<ConfirmationAction | null>(null);

    const { isPending: isApprovePending, mutate: approveOrder } = useApprovePayoutOrder({
        onSuccess: () => {
            setConfirmationAction(null);
            onApprove?.();
        },
    });

    const { isPending: isDeclinePending, mutate: declineOrder } = useDeclinePayoutOrder({
        onSuccess: () => {
            setConfirmationAction(null);
            onDecline?.();
        },
    });

    const { isPending: isInvestigatePending, mutate: investigateOrder } = useInvestigatePayoutOrder({
        onSuccess: () => {
            setConfirmationAction(null);
        },
    });

    const isPending = isDeclinePending || isApprovePending || isInvestigatePending;
    const actionConfig: Record<ConfirmationAction, ConfirmationActionConfig> = {
        investigate: {
            title: 'Перевод ордера в арбитраж',
            message: 'Ордер будет переведен в арбитраж.',
            confirmText: 'Перевести в арбитраж',
            loading: isInvestigatePending,
            submit: (orderId) => investigateOrder({ orderId }),
        },
        approve: {
            title: 'Подтверждение оплаты',
            message: 'Оплата по ордеру будет подтверждена.',
            confirmText: 'Подтвердить оплату',
            loading: isApprovePending,
            submit: (orderId) => approveOrder({ orderId }),
        },
        decline: {
            title: 'Отклонение ордера',
            message: 'Ордер будет отклонен.',
            confirmText: 'Отклонить',
            loading: isDeclinePending,
            submit: (orderId) => declineOrder({ orderId }),
        },
    };
    const confirmation = confirmationAction ? actionConfig[confirmationAction] : null;

    const openConfirmation = (action: ConfirmationAction) => {
        setConfirmationAction(action);
    };

    const openInvestigateConfirmation = () => {
        openConfirmation('investigate');
    };

    const openApproveConfirmation = () => {
        openConfirmation('approve');
    };

    const openDeclineConfirmation = () => {
        openConfirmation('decline');
    };

    const handleConfirmAction = () => {
        const orderId = order.id;

        if (!confirmationAction || typeof orderId?.value !== 'string') {
            return;
        }

        actionConfig[confirmationAction].submit(orderId);
    };

    const handleCancelConfirmation = () => {
        setConfirmationAction(null);
    };

    return {
        confirmationDialogProps: {
            opened: Boolean(confirmation),
            title: confirmation?.title,
            message: confirmation?.message,
            confirmText: confirmation?.confirmText,
            cancelText: 'Отмена',
            loading: confirmation?.loading,
            onConfirm: handleConfirmAction,
            onCancel: handleCancelConfirmation,
        },
        isApprovePending,
        isDeclinePending,
        isInvestigatePending,
        isPending,
        openApproveConfirmation,
        openDeclineConfirmation,
        openInvestigateConfirmation,
    };
};
